import { useCustom } from '@refinedev/core';
import { Card, Form, InputNumber, Switch, Button, Row, Col, message, Spin } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { apiClient } from '../../lib/api-client';
import { LabelWithHelp, SectionTitle } from '../../components/ui/HelpTooltip';

interface GlobalSettings {
  // Commission
  intercityCommission: number;
  interregionCommission: number;
  postageCommission: number;
  cargoCommission: number;

  // Pricing
  defaultPricePerKm: number;

  // Auto-find
  offerTimeoutMinutes: number;
  progressiveOfferMinutes: number;
  maxDriversPerOffer: number;
  driverSearchRadiusKm: number;

  // Negotiation
  maxCounterOffers: number;
  counterOfferTimeoutSeconds: number;

  // Driver settings
  minDriverRating: number;
  maxCancellationsBeforeBlock: number;
  blockDurationHours: number;
  driverCancellationPenalty: number;

  // Client settings
  clientCancellationPenalty: number;
  clientNoShowWaitMinutes: number;

  // Driver rest
  longTripThresholdKm: number;
  restMinutesPerKm: number;
  restMinutesPerHour: number;
  maxRestMinutes: number;

  // Surge pricing
  surgePricingEnabled: boolean;
  surgeThresholdRatio: number;
  surgeMinMultiplier: number;
  surgeMaxMultiplier: number;

  // Night pricing
  nightPricingEnabled: boolean;
  nightStartHour: number;
  nightEndHour: number;
  nightPriceMultiplier: number;

  // Referral
  referralBonusClient: number;
  referralBonusDriver: number;

  // Postage
  maxPostageDestinations: number;
  maxPackageWeightKg: number;

  // Limits
  maxTripDistanceKm: number;
  locationUpdateIntervalMs: number;
}

export const Settings = () => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const { data, isLoading, refetch } = useCustom<GlobalSettings>({
    url: '/admin/settings',
    method: 'get',
  });

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await apiClient.put('/admin/settings', values);
      message.success('Sozlamalar saqlandi');
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Sozlamalar</h2>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={saving}
        >
          Saqlash
        </Button>
      </div>

      <Form form={form} layout="vertical" initialValues={data?.data}>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Card
              title={
                <SectionTitle
                  title="Komissiya (foizda)"
                  help="Har bir sayohat turidan olinadigan komissiya foizi. Bu summa haydovchi hisobidan ushlab qolinadi."
                />
              }
              style={{ marginBottom: 24 }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Shahar ichida"
                        help="Shahar ichidagi taksi sayohatlaridan olinadigan komissiya foizi"
                      />
                    }
                    name="intercityCommission"
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      suffix="%"
                      placeholder="10"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Shaharlararo"
                        help="Shaharlararo taksi sayohatlaridan olinadigan komissiya foizi"
                      />
                    }
                    name="interregionCommission"
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      suffix="%"
                      placeholder="12"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Pochta"
                        help="Pochta yetkazib berish xizmatidan olinadigan komissiya foizi"
                      />
                    }
                    name="postageCommission"
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      suffix="%"
                      placeholder="15"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Yuk"
                        help="Yuk tashish xizmatidan olinadigan komissiya foizi"
                      />
                    }
                    name="cargoCommission"
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      suffix="%"
                      placeholder="15"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card
              title={
                <SectionTitle
                  title="Avto-qidiruv"
                  help="Haydovchilarni avtomatik qidirish va taklif yuborish sozlamalari"
                />
              }
              style={{ marginBottom: 24 }}
            >
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Taklif vaqti (daqiqa)"
                    help="Haydovchiga yuborilgan taklifning amal qilish muddati (daqiqada)"
                  />
                }
                name="offerTimeoutMinutes"
              >
                <InputNumber min={1} placeholder="10" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Progressiv taklif (daqiqa)"
                    help="Agar birinchi haydovchi qabul qilmasa, qancha vaqtdan keyin keyingi haydovchilarga taklif yuboriladi"
                  />
                }
                name="progressiveOfferMinutes"
              >
                <InputNumber min={1} placeholder="2" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Maksimal haydovchilar"
                    help="Bir vaqtda nechta haydovchiga taklif yuborilishi mumkin"
                  />
                }
                name="maxDriversPerOffer"
              >
                <InputNumber min={1} placeholder="5" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Qidiruv radiusi (km)"
                    help="Mijoz atrofida qancha km radiusda haydovchilar qidiriladi"
                  />
                }
                name="driverSearchRadiusKm"
              >
                <InputNumber min={1} placeholder="10" style={{ width: '100%' }} />
              </Form.Item>
            </Card>

            <Card
              title={
                <SectionTitle
                  title="Muzokara"
                  help="Narx bo'yicha muzokara qilish sozlamalari"
                />
              }
              style={{ marginBottom: 24 }}
            >
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Maksimal qarama-qarshi takliflar"
                    help="Mijoz va haydovchi o'rtasida necha marta narx taklifi almashishi mumkin"
                  />
                }
                name="maxCounterOffers"
              >
                <InputNumber min={1} placeholder="3" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Taklif vaqti (soniya)"
                    help="Qarama-qarshi taklifga javob berish uchun berilgan vaqt (soniyada)"
                  />
                }
                name="counterOfferTimeoutSeconds"
              >
                <InputNumber min={30} placeholder="180" style={{ width: '100%' }} />
              </Form.Item>
            </Card>

            <Card
              title={
                <SectionTitle
                  title="Haydovchi sozlamalari"
                  help="Haydovchilar uchun reyting va jazo sozlamalari"
                />
              }
              style={{ marginBottom: 24 }}
            >
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Minimal reyting"
                    help="Haydovchi buyurtma olishi uchun minimal reyting (1-5)"
                  />
                }
                name="minDriverRating"
              >
                <InputNumber min={1} max={5} step={0.1} placeholder="3.5" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Bloklashdan oldin bekor qilishlar"
                    help="Haydovchi necha marta buyurtmani bekor qilgandan keyin bloklanadi"
                  />
                }
                name="maxCancellationsBeforeBlock"
              >
                <InputNumber min={1} placeholder="5" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Bloklash muddati (soat)"
                    help="Haydovchi qancha soatga bloklanadi"
                  />
                }
                name="blockDurationHours"
              >
                <InputNumber min={1} placeholder="24" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Bekor qilish jarimasi (so'm)"
                    help="Haydovchi buyurtmani bekor qilganda olinadigan jarima summasi"
                  />
                }
                name="driverCancellationPenalty"
              >
                <InputNumber min={0} placeholder="5000" style={{ width: '100%' }} />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              title={
                <SectionTitle
                  title="Surge narxlash"
                  help="Talab ko'p bo'lganda narxlarni avtomatik oshirish tizimi. Haydovchilar kam va mijozlar ko'p bo'lganda yoqiladi."
                />
              }
              style={{ marginBottom: 24 }}
            >
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Yoqilgan"
                    help="Surge narxlash tizimini yoqish yoki o'chirish"
                  />
                }
                name="surgePricingEnabled"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Chegara nisbati"
                    help="Surge yoqilishi uchun mijozlar/haydovchilar nisbati. Masalan: 2 = 2 mijozga 1 haydovchi"
                  />
                }
                name="surgeThresholdRatio"
              >
                <InputNumber min={1} step={0.1} placeholder="2.0" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Minimal ko'paytiruvchi"
                    help="Surge boshlanganida eng kam narx ko'paytiruvchisi. Masalan: 1.2 = 20% qimmatroq"
                  />
                }
                name="surgeMinMultiplier"
              >
                <InputNumber min={1} step={0.1} placeholder="1.2" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Maksimal ko'paytiruvchi"
                    help="Surge eng yuqori bo'lganda narx ko'paytiruvchisi. Masalan: 2.0 = 2 baravar qimmat"
                  />
                }
                name="surgeMaxMultiplier"
              >
                <InputNumber min={1} step={0.1} placeholder="2.0" style={{ width: '100%' }} />
              </Form.Item>
            </Card>

            <Card
              title={
                <SectionTitle
                  title="Tungi narxlash"
                  help="Kechasi ma'lum soatlarda narxlarni oshirish. Odatda 22:00 dan 06:00 gacha."
                />
              }
              style={{ marginBottom: 24 }}
            >
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Yoqilgan"
                    help="Tungi narxlash tizimini yoqish yoki o'chirish"
                  />
                }
                name="nightPricingEnabled"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Boshlanish soati"
                        help="Tungi narxlash qaysi soatda boshlanadi (0-23)"
                      />
                    }
                    name="nightStartHour"
                  >
                    <InputNumber min={0} max={23} placeholder="22" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Tugash soati"
                        help="Tungi narxlash qaysi soatda tugaydi (0-23)"
                      />
                    }
                    name="nightEndHour"
                  >
                    <InputNumber min={0} max={23} placeholder="6" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Narx ko'paytiruvchi"
                    help="Tungi vaqtda narx qancha marta ko'payadi. Masalan: 1.3 = 30% qimmatroq"
                  />
                }
                name="nightPriceMultiplier"
              >
                <InputNumber min={1} step={0.1} placeholder="1.3" style={{ width: '100%' }} />
              </Form.Item>
            </Card>

            <Card
              title={
                <SectionTitle
                  title="Referal bonuslar (so'm)"
                  help="Do'stlarini taklif qilgan foydalanuvchilarga beriladigan bonus summasi"
                />
              }
              style={{ marginBottom: 24 }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Mijoz uchun"
                        help="Yangi mijoz taklif qilgan foydalanuvchiga beriladigan bonus"
                      />
                    }
                    name="referralBonusClient"
                  >
                    <InputNumber min={0} placeholder="10000" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label="Haydovchi uchun"
                        help="Yangi haydovchi taklif qilgan foydalanuvchiga beriladigan bonus"
                      />
                    }
                    name="referralBonusDriver"
                  >
                    <InputNumber min={0} placeholder="20000" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card
              title={
                <SectionTitle
                  title="Mijoz sozlamalari"
                  help="Mijozlar uchun jazo va kutish vaqti sozlamalari"
                />
              }
              style={{ marginBottom: 24 }}
            >
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Bekor qilish jarimasi (so'm)"
                    help="Mijoz buyurtmani bekor qilganda olinadigan jarima summasi"
                  />
                }
                name="clientCancellationPenalty"
              >
                <InputNumber min={0} placeholder="3000" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Kutish vaqti (daqiqa)"
                    help="Haydovchi yetib kelganidan keyin mijozni qancha daqiqa kutadi"
                  />
                }
                name="clientNoShowWaitMinutes"
              >
                <InputNumber min={1} placeholder="5" style={{ width: '100%' }} />
              </Form.Item>
            </Card>

            <Card
              title={
                <SectionTitle
                  title="Pochta va yuk limitlari"
                  help="Pochta va yuk tashish xizmati uchun limitlar"
                />
              }
              style={{ marginBottom: 24 }}
            >
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Maksimal manzillar (pochta)"
                    help="Bir pochta buyurtmasida nechta manzil ko'rsatish mumkin"
                  />
                }
                name="maxPostageDestinations"
              >
                <InputNumber min={1} placeholder="5" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label={
                  <LabelWithHelp
                    label="Maksimal og'irlik (kg)"
                    help="Bir paket/yuk uchun maksimal og'irlik kilogrammda"
                  />
                }
                name="maxPackageWeightKg"
              >
                <InputNumber min={1} placeholder="50" style={{ width: '100%' }} />
              </Form.Item>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};
